import React, { useMemo } from 'react';
import './pagination.css';

/** Default number of page number buttons to show (e.g. 1 2 3 4) */
export const MAX_VISIBLE_PAGES = 4;

/**
 * Reusable pagination component matching the transaction history style:
 * Previous | 1 2 3 4 | Next with active page in pink, inactive in dark grey.
 * Use anywhere pagination is needed (Transactions, Monthly Earnings, Daily Earnings, Payout History).
 *
 * @param {number} currentPage - 1-based current page
 * @param {number} totalPages - Total number of pages
 * @param {function(number)} onPageChange - Called with page number when a page is clicked
 * @param {function} onPreviousPage - Called when Previous is clicked
 * @param {function} onNextPage - Called when Next is clicked
 * @param {number} [maxVisiblePages=4] - Max page number buttons to show
 */
export const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  onPreviousPage,
  onNextPage,
  maxVisiblePages = MAX_VISIBLE_PAGES,
}) => {
  const pageNumbers = useMemo(() => {
    const pages = [];
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <span
          key={i}
          className={`modern-transactions-page-number ${i === currentPage ? 'active' : ''}`}
          onClick={() => onPageChange(i)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onPageChange(i);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={`Page ${i}`}
          aria-current={i === currentPage ? 'page' : undefined}
        >
          {i}
        </span>
      );
    }

    return pages;
  }, [currentPage, totalPages, onPageChange, maxVisiblePages]);

  if (totalPages <= 1) {
    return null;
  }

  return (
    <div className="modern-transactions-pagination" role="navigation" aria-label="Pagination">
      <button
        type="button"
        className={`modern-transactions-pagination-btn ${currentPage === 1 ? 'disabled' : ''}`}
        onClick={onPreviousPage}
        disabled={currentPage === 1}
        aria-label="Previous page"
      >
        <i className="fa-solid fa-chevron-left modern-transactions-pagination-icon" aria-hidden />
        Previous
      </button>

      <div className="modern-transactions-page-numbers">{pageNumbers}</div>

      <button
        type="button"
        className={`modern-transactions-pagination-btn ${currentPage === totalPages ? 'disabled' : ''}`}
        onClick={onNextPage}
        disabled={currentPage === totalPages}
        aria-label="Next page"
      >
        Next
        <i className="fa-solid fa-chevron-right modern-transactions-pagination-icon" aria-hidden />
      </button>
    </div>
  );
};

/**
 * Renders pagination buttons (Prev | 1 2 3 | Next) with icon-only prev/next.
 * Uses classes: pagination-btn, prev-btn, page-btn, next-btn, active.
 * Use with all-creators-pagination wrapper or similar.
 *
 * @param {number} currentPage - 1-based current page
 * @param {number} totalPages - Total number of pages
 * @param {function(number)} onPageChange - Called with page number when a page is clicked
 * @param {number} [maxVisiblePages=3] - Max page number buttons to show
 */
export const renderPaginationButtons = (currentPage, totalPages, onPageChange, maxVisiblePages = 3) => {
  const buttons = [];
  const goToPage = (page) => {
    if (!Number.isInteger(page) || page < 1 || page > totalPages || page === currentPage) {
      return;
    }
    onPageChange(page);
  };

  buttons.push(
    <button
      key="prev"
      className="pagination-btn prev-btn"
      onClick={() => goToPage(currentPage - 1)}
      disabled={currentPage === 1}
      type="button"
      aria-label="Previous page"
    >
      <i className="fa-solid fa-chevron-left" aria-hidden />
    </button>
  );

  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    buttons.push(
      <button
        key={i}
        type="button"
        className={`pagination-btn page-btn ${currentPage === i ? 'active' : ''}`}
        onClick={() => goToPage(i)}
        aria-label={`Page ${i}`}
        aria-current={currentPage === i ? 'page' : undefined}
      >
        {i}
      </button>
    );
  }

  buttons.push(
    <button
      key="next"
      className="pagination-btn next-btn"
      onClick={() => goToPage(currentPage + 1)}
      disabled={currentPage === totalPages}
      type="button"
      aria-label="Next page"
    >
      <i className="fa-solid fa-chevron-right" aria-hidden />
    </button>
  );

  return buttons;
};
